import { formatValue } from '@/charts/palette'
import { fmtNumber, useT } from '@/i18n'
import { uiMessages } from '@/i18n/messages/ui'

interface Props {
  label: string
  value: number
  unit: string
  /**
   * Percent change vs the preceding window.
   *   number    — render the delta
   *   null      — a comparison was attempted and there is no baseline; say so
   *   undefined — this tile has no comparison by design; render nothing
   *
   * The distinction matters: five tiles all captioned "no prior period" is noise that
   * trains people to ignore the line where it does carry meaning.
   */
  changePct?: number | null
  /** Set when a higher number is worse, so the arrow's colour matches the meaning. */
  lowerIsBetter?: boolean
  /** Replaces "vs prior period" when the delta compares against something else (e.g. the company). */
  compareLabel?: string
  /** Pre-formatted hero text, for a value formatValue can't express (e.g. one decimal). */
  display?: string
}

/**
 * Stat tile: label (sentence case, no colon), hero value, optional delta.
 *
 * No sparkline and no chart — this is the form for "one number is the answer".
 * The delta carries an arrow glyph as well as colour, so the direction survives
 * for a colourblind reader and in forced-colours mode.
 */
export function StatTile({ label, value, unit, changePct, lowerIsBetter = false, compareLabel, display }: Props) {
  const t = useT(uiMessages)
  const improving = changePct == null ? null : lowerIsBetter ? changePct < 0 : changePct > 0

  return (
    <div className="rounded-2xl bg-white p-3 ring-1 ring-neutral-200 sm:p-4 dark:bg-neutral-900 dark:ring-neutral-800">
      <div className="text-xs text-neutral-500">{label}</div>
      <div className="tabular mt-1 text-xl font-semibold tracking-tight text-neutral-900 sm:text-2xl dark:text-neutral-100">
        {display ?? formatValue(value, unit)}
      </div>
      {changePct != null && Number.isFinite(changePct) ? (
        <div
          className="mt-1 flex items-center gap-1 text-xs"
          style={{ color: improving ? 'var(--viz-good)' : 'var(--viz-serious)' }}
        >
          <span aria-hidden>{changePct > 0 ? '▲' : changePct < 0 ? '▼' : '—'}</span>
          <span className="tabular">
            {fmtNumber(Math.abs(changePct), 1)}%
          </span>
          <span className="text-neutral-500">{compareLabel ?? t('vsPriorPeriod')}</span>
        </div>
      ) : changePct === null ? (
        <div className="mt-1 text-xs text-neutral-400">{t('noPriorPeriod')}</div>
      ) : null}
    </div>
  )
}
